import { Injectable } from '@angular/core';
import { OpenAiService } from '@maximizer/core/shared/data-access';
import { MaximizerAiService } from '@maximizer/ai/data-access';
import { OpenAI } from '@maximizer/core/shared/domain';
import { Observable, map, catchError, of, switchMap } from 'rxjs';
import {
  AI_SIGNATURE_FALLBACK_PROMPT,
  DEFAULT_OPENAI_MODEL,
  containsRestrictedDataMask,
  createRedactionState,
  redactRestrictedData,
} from '@maximizer/outlook/shared/domain';

export interface EmailSignatureFields {
  firstName?: string;
  lastName?: string;
  company?: string;
  position?: string;
  website?: string;
  phones?: string[];
  address?: string;
}

export const AI_EXTRACTION_MIN_BODY_LENGTH = 10;
const MAX_BODY_CHARACTER_LENGTH = 50000;
const SIGNATURE_EXTRACT_PROMPT_NAME = 'outlook-email-signature-extract';

@Injectable()
export class EmailSignatureAiService {
  private readonly nonNameCharacterRegex = /[^A-Za-z0-9.\-\s]/g;
  private readonly nonPhoneCharacterRegex = /[^\d+x\s-]/gi;
  private readonly phoneExtensionRegex = /x(\d+)/i;
  private readonly normalizedPhoneExtensionReplacement = 'x$1';
  private readonly emailIndicatorRegex = /@/;
  private readonly localPartNameSeparatorRegex = /[._-]/;
  private readonly trailingWebsitePunctuationRegex = /[).,;]/;

  private readonly genericDomains = new Set([
    'gmail.com',
    'googlemail.com',
    'outlook.com',
    'icloud.com',
    'yahoo.com',
    'yahoo.ca',
    'hotmail.com',
    'live.com',
    'msn.com',
    'me.com',
    'mac.com',
    'ymail.com',
    'rocketmail.com',
    'yahoo.co.uk',
    'yahoo.co.in',
    'mail.com',
    'zoho.com',
    'fastmail.com',
    'hey.com',
  ]);

  constructor(
    private readonly maximizerAi: MaximizerAiService,
    private readonly openAi: OpenAiService,
  ) {}

  extract(
    targetEmail: string,
    fullName: string | null,
    body: string,
  ): Observable<EmailSignatureFields | null> {
    if (body.length > MAX_BODY_CHARACTER_LENGTH) {
      console.warn(
        `[EmailSignatureAI] Max body thread exceeded: ${body.length} characters (limit: ${MAX_BODY_CHARACTER_LENGTH})`,
      );
      return of(null);
    }

    return this.maximizerAi
      .lookupPrompts({
        project: 'Outlook',
        filter: { name: [SIGNATURE_EXTRACT_PROMPT_NAME] },
      })
      .pipe(
        catchError(() => of([])),
        switchMap((lookupResults) => {
          const lookedUpPrompt = lookupResults[0];
          const config =
            (lookedUpPrompt?.config as OpenAI.RequestConfiguration) ?? {};
          const requestConfig: OpenAI.RequestConfiguration = {
            model: config.model ?? DEFAULT_OPENAI_MODEL,
            max_tokens: config.max_tokens ?? 500,
            temperature: config.temperature ?? 0.1,
            response_format: { type: 'json_object' },
          };

          const contextInfo = [
            `Target contact email (exact match only): ${targetEmail}`,
            ...(fullName
              ? [`Target contact name hint (secondary): ${fullName}`]
              : []),
            'Only extract values that belong to this exact email and ignore other participants.',
            'The email match is primary. Use name only as supporting evidence.',
          ];

          return this.openAi.summarize<EmailSignatureFields>(
            contextInfo,
            [redactRestrictedData(body, createRedactionState())],
            lookedUpPrompt?.prompt || AI_SIGNATURE_FALLBACK_PROMPT,
            requestConfig,
          );
        }),
        map((aiResult) => {
          if (!aiResult) {
            return null;
          }
          const processed = this.postProcess(aiResult, targetEmail);
          return processed;
        }),
        catchError((err) => {
          console.error('[EmailSignatureAI] Extract failed:', err);
          return of(null);
        }),
      );
  }

  private postProcess(
    result: EmailSignatureFields,
    senderEmail: string,
  ): EmailSignatureFields {
    const sanitize = (value?: string) =>
      value ? value.replace(this.nonNameCharacterRegex, '').trim() : value;

    const unredacted = this.dropRedactedFields(result);

    const { firstName, lastName } = this.processNameFields(
      unredacted,
      senderEmail,
    );

    let company = unredacted.company;
    if (!company) {
      const domain = senderEmail.split('@')[1] ?? '';
      if (domain && !this.genericDomains.has(domain)) {
        const host = domain.split('.')[0];
        if (host && host.length > 1) {
          company = host.charAt(0).toUpperCase() + host.slice(1);
        }
      }
    }

    const phones = (unredacted.phones ?? [])
      .map((phone) =>
        phone
          .replace(this.nonPhoneCharacterRegex, '')
          .replace(
            this.phoneExtensionRegex,
            this.normalizedPhoneExtensionReplacement,
          ),
      )
      .filter(Boolean)
      .slice(0, 2);

    return {
      firstName: sanitize(firstName),
      lastName: sanitize(lastName),
      company: sanitize(company),
      position: sanitize(unredacted.position),
      website: this.cleanWebsiteUrl(sanitize(unredacted.website)),
      phones,
      address: sanitize(unredacted.address),
    };
  }

  // Tier 1 redaction is one-way, so a field the model echoed a placeholder
  // into carries no usable value; sanitize() would strip the angle brackets
  // and leave `MXSIN1` in the form.
  private dropRedactedFields(
    result: EmailSignatureFields,
  ): EmailSignatureFields {
    const keepUnredacted = (value?: string) =>
      value && containsRestrictedDataMask(value) ? undefined : value;

    return {
      ...result,
      firstName: keepUnredacted(result.firstName),
      lastName: keepUnredacted(result.lastName),
      company: keepUnredacted(result.company),
      position: keepUnredacted(result.position),
      website: keepUnredacted(result.website),
      address: keepUnredacted(result.address),
      phones: result.phones?.filter(
        (phone) => !containsRestrictedDataMask(phone),
      ),
    };
  }

  private processNameFields(
    result: EmailSignatureFields,
    senderEmail: string,
  ): { firstName: string; lastName: string } {
    let { firstName, lastName } = result;

    if (lastName && this.emailIndicatorRegex.test(lastName)) {
      lastName = '';
    }

    if (firstName && !lastName) {
      lastName = firstName;
      firstName = '';
    } else if (!firstName && !lastName) {
      const local = senderEmail.split('@')[0];
      const parts = local
        .split(this.localPartNameSeparatorRegex)
        .filter(Boolean);

      if (parts.length === 1) {
        lastName = parts[0];
      } else if (parts.length >= 2) {
        firstName = parts[0];
        lastName = parts.at(-1);
      }
    }

    return { firstName: firstName ?? '', lastName: lastName ?? '' };
  }

  private cleanWebsiteUrl(url?: string): string {
    if (!url) return '';
    let cleaned = url;
    while (
      cleaned.length > 0 &&
      this.trailingWebsitePunctuationRegex.test(cleaned.slice(-1))
    ) {
      cleaned = cleaned.slice(0, -1);
    }

    return cleaned;
  }
}
